import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import classNames from 'classnames';
import styles from './product-grid-2.module.scss';
import { useServices } from '@/services-context';
import ProductElement from '../product-element';
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import ProductCanBe from "@/elements-react/product-can-be";
import { getFilteredCrosses } from "@/helpers/product-manager.ts";
import triangle from "@/assets/triangle.svg";
import { Icon } from "@/elements-react/fix-icon";
import { useTranslation } from "react-i18next";

type ProductItem = {
  id: string;
  name: string;
  mpn: string;
  brand: string;
  price: [number, number];
  discount: number;
  images: Array<{ path: string }>;
  url: string;
  exchanged: boolean;
  exchanges: Array<ProductItem>;
  crosses: Array<ProductItem>;
};

export type ProductGrid2Props = {
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  data: Array<ProductItem>;
  googleEventData: Array<{
    item_name: string;
    item_id: string;
    price: number;
    item_brand: string;
    index: number;
    quantity: string;
    [key: `item_category${string}`]: string | undefined;
  }>;
  unitedSearchResult?: boolean;
};

const LG_CARD_WIDTH = 213;
const LG_CARD_MAX_WIDTH = LG_CARD_WIDTH * 3;
const LG_GAP = 16;
const SM_CARD_WIDTH = 168;
const SM_CARD_MAX_WIDTH = SM_CARD_WIDTH * 3;
const SM_GAP = 8;

function calcSize(card: number, gap: number, count: number) {
  return card * count + gap * (count - 1);
}

function getSizeClassNames(type: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM, width: number) {
  const cardWidth = type === LayoutStyleTypes.TYPE_LG ? LG_CARD_WIDTH : SM_CARD_WIDTH;
  const cardMaxWidth = type === LayoutStyleTypes.TYPE_LG ? LG_CARD_MAX_WIDTH : SM_CARD_MAX_WIDTH;
  const gap = type === LayoutStyleTypes.TYPE_LG ? LG_GAP : SM_GAP;

  return {
    'col-8': width > calcSize(cardWidth, gap, 8),
    'col-4': width > calcSize(cardWidth, gap, 4) && width <= calcSize(cardWidth, gap, 8),
    'col-2': width > calcSize(cardWidth, gap, 2) && width <= calcSize(cardWidth, gap, 4),
    'col-1': width <= calcSize(cardWidth, gap, 2),
    limit: width > calcSize(cardMaxWidth, gap, 8),
  };
}

export const ProductGrid2: React.FC<ProductGrid2Props> = ({ type = LayoutStyleTypes.TYPE_LG, data, googleEventData, unitedSearchResult }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { layoutType } = useLayoutType();
  const isMobile = layoutType === LayoutTypes.TYPE_MOBILE;

  const [classMap, setClassMap] = useState<{ [key: string]: boolean }>({ 'col-1': isMobile, 'col-2': false, 'col-4': !isMobile, 'col-8': false, limit: false });
  const services = useServices();
  const { t } = useTranslation();

  // SSR-safe measure
  const measure = useCallback(() => {
    if (typeof window === 'undefined' || !ref.current) return;
    const width = ref.current.getBoundingClientRect().width;
    setClassMap(getSizeClassNames(type, width));
  }, [type]);

  useEffect(() => {
    measure();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const gridStyle = useMemo(() => {
    if (type === LayoutStyleTypes.TYPE_LG) {
      return { gap: `${LG_GAP}px`, minWidth: `${LG_CARD_WIDTH}px` };
    } else {
      return { gap: `${SM_GAP}px`, minWidth: `${SM_CARD_WIDTH}px` };
    }
  }, [type]);

  const [products, exchanges] = useMemo(() => {
    const productsItems: ProductItem[] = [];
    const exchanges: ProductItem[] = [];

    data.forEach((item) => {
      if (item.exchanged) {
        const exchangeProduct = item.exchanges[0];
        exchangeProduct.crosses = item.crosses;
        productsItems.push(exchangeProduct);
      } else {
        productsItems.push(item);

        if (item.exchanges.length > 0) {
          exchanges.push(...item.exchanges);
        }
      }
    });

    return [productsItems, exchanges];
  }, [data]);

  const renderUnitedElement = (productItem: ProductItem, index: number) => {
    const filteredCrosses = getFilteredCrosses(productItem);
    const itemGoogleEvent = googleEventData.find((product) => product.item_id === productItem!.id);
    return (
      <div
        key={index.toString()}
        className={classNames(
          styles.container,
          classMap["col-8"] && styles["col-8"],
          classMap["col-4"] && styles["col-4"],
          classMap["col-2"] && styles["col-2"],
          classMap["col-1"] && styles["col-1"],
          classMap.limit && styles.limit,
          styles["primary-product"],
        )}
        style={classMap.limit && type === LayoutStyleTypes.TYPE_LG ? { ...gridStyle, maxWidth: `${LG_CARD_MAX_WIDTH}px` } : gridStyle}
      >
        <ProductElement
          {...productItem}
          googleEventData={itemGoogleEvent}
          type={type}
          productId={productItem!.id}
          href={productItem!.url}
          isPrimaryType={true}
        />
        <div
          className={classNames(
            styles["analogue"],
            classMap["col-4"] && styles["analogue-col-4"],
            classMap["col-2"] && styles["analogue-col-2"],
            classMap["col-1"] && styles["analogue-col-1"],
          )}
        >
          {!filteredCrosses.length ? (
            <div className={styles["analogue-container"]}>
              <Icon className={styles["analogues-icon"]} src={triangle} width={24} />
              <div className={classNames([styles["analogues-not-found"], styles[type]])}>{t("893ijvjn4nvu31iz.no-analogues-found", "No analogues found")}</div>
            </div>
          ) : (
            <div>
              <Icon className={styles["analogues-icon"]} src={triangle} width={24} />
              <div className={classNames([styles["analogues-label"], styles[type]])}>{t("nxx123sdfsd3fvbn.analogues", "Analogues")}</div>
              <ProductCanBe data={filteredCrosses} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDefaultResult = () => {
    return (
      <div>
        <div
          ref={ref}
          className={classNames(
            styles.container,
            classMap['col-8'] && styles['col-8'],
            classMap['col-4'] && styles['col-4'],
            classMap['col-2'] && styles['col-2'],
            classMap['col-1'] && styles['col-1'],
            classMap.limit && styles.limit,
          )}
          style={classMap.limit && type === LayoutStyleTypes.TYPE_LG ? { ...gridStyle, maxWidth: `${LG_CARD_MAX_WIDTH}px` } : gridStyle}
        >
          {products.map((item, i) => {
            const itemGoogleEvent = googleEventData.find((product) => product.item_id === item.id);
            return <ProductElement key={item.id} {...item} googleEventData={itemGoogleEvent} type={type} productId={item.id} href={item.url} />;
          })}
        </div>
      </div>
    );
  };

  const renderUnitedResult = () => {
    return (
      <div>
        {products.map((item, i) => renderUnitedElement(item, i))}
        {!!exchanges.length && (
          <div className={classNames([styles["exchanges-label"], styles[type]])}>{t("kn23jv843mv9sjvs.exchanges", "Exchanges")}</div>
        )}
        {!!exchanges.length && (
          <div
            ref={ref}
            className={classNames(
              styles.container,
              classMap['col-8'] && styles['col-8'],
              classMap['col-4'] && styles['col-4'],
              classMap['col-2'] && styles['col-2'],
              classMap['col-1'] && styles['col-1'],
              classMap.limit && styles.limit,
            )}
            style={classMap.limit && type === LayoutStyleTypes.TYPE_LG ? { ...gridStyle, maxWidth: `${LG_CARD_MAX_WIDTH}px` } : gridStyle}
          >
            {exchanges.map((item, i) => {
              const itemGoogleEvent = googleEventData.find((product) => product.item_id === item.id);
              return <ProductElement key={item.id} {...item} googleEventData={itemGoogleEvent} type={type} productId={item.id} href={item.url} />;
            })}
          </div>
        )}
      </div>
    );
  };

  return unitedSearchResult ? renderUnitedResult() : renderDefaultResult();
};

export default ProductGrid2;
